import React, { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AccountCard from '../../components/AccountCard';
import CardCategories from '../../components/CardCategories';
import CardSelect from './components/CardSelect';
import Button from '../../../../components/global/Button';
import useAccountCardConfigure from './useAccountCardConfigure';
import AccountCardDismissConfirm from './components/AccountCardDismissConfirm/AccountCardDismissConfirm';
import useAccountCardCheckout from './useAccountCardCheckout';
import AccountCardCheckoutSuccessAlert from './components/AccountCardCheckoutSuccessAlert';
import Angle from '../../../../components/global/Angle';
import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../constants';
import useIsNativeWebView from '../../../native-bridge/hooks/useIsWebView';
import {
  postNativeAppCardsConfiguratorComplete,
  postNativeAppCardsConfiguratorDismiss,
} from '../../../native-bridge/native-apps-interface';
import ContactSpreadsheetImportModal from '../../components/ContactSpreadsheetImport/modal';
import CardSideSelect from './components/CardSideSelect';
import { canSelectBackCardSideGroups } from './helpers';
import useVirtualPageView from '../../../../apps/pro/services/analytics/useVirtualPageView';
import AccountCardCheckoutPayment from './components/AccountCardCheckoutPayment/modal';
import AccountCardReview from './components/AccountCardReview';
import AccountCardAddressBookTable from './components/AccountCardAddressBookTable/modal';
import { mapRosterIdentitiesToCustomPrintRecipients } from '../../../../apps/pro/views/AccountAddressBook/components/AccountAddressBookTable/helpers';
import {
  getAccountCardElementsConfiguration,
  hasPendingLabeledTextElementsOnSide,
  isCardElementEditable,
} from '../../components/AccountCard/helpers';
import useBasename from '../../../../components/routing/useBasename';
import { BASENAME } from '../../../../constants/basenames';
import CardElementPhotoSelectionControl from '../../components/CardElementPhoto/CardElementPhotoSelectionControl';
import CardElementAddressControl from '../../components/CardElementAddress/CardElementAddressControl/CardElementAddressControl';
import CardElementStickerControl from '../../components/CardElementSticker/CardElementStickerControl';
import AccountCardAddToCartSuccess from './components/AccountCardAddToCartSuccess/modal';
import DismissButton from '../../../../components/global/DismissButton';
import AccountCardStampConfirm from './components/AccountCardStampConfirm/AccountCardStampConfirm';
import AccountCardStickerConfirm from './components/AccountCardStickerConfirm/AccountCardStickerConfirm';
import useOpenModal from '../../../../hooks/useOpenModal';
import { CHECKOUT_CART_MODAL } from '../../../checkout/components/CheckoutCart/modal';
import useAccountCardCreations from '../../../../apps/pro/views/AccountCards/hooks/useAccountCardCreations';
import AccountCardList from '../../../../apps/pro/views/AccountCards/components/AccountCardList';
import AccountCardForm from './components/AccountCardForm/AccountCardForm';

const propTypes = {
  className: PropTypes.string,
  accountCardId: PropTypes.string,
  accountCardAlbumFolderId: PropTypes.string,
  onExit: PropTypes.func,
  onAccountCardChange: PropTypes.func,
  presetCardElementConfigurationInputs: PropTypes.arrayOf(
    PropTypes.shape({
      cardElement: PropTypes.shape({
        type: PropTypes.string.isRequired,
      }).isRequired,
      cardElementConfiguration: PropTypes.object.isRequired,
    }),
  ),
  photosContext: PropTypes.shape({
    account_id: PropTypes.string,
    album_id: PropTypes.string,
    identity_id: PropTypes.string,
    roster_identity_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  defaultCardCategoryId: PropTypes.string,
  defaultParentCardCategoryId: PropTypes.string,
  sharingEnabled: PropTypes.bool,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  hideHeader: PropTypes.bool,
  onAccountCardChanged: PropTypes.func,
  onReviewModeChanged: PropTypes.func,
  allowReviewing: PropTypes.bool,
  reviewProps: PropTypes.shape({
    onCheckout: PropTypes.func,
  }),
  onAddToCart: PropTypes.func,
  hasCartItems: PropTypes.bool,
  mappings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
  onCheckoutSuccess: PropTypes.func,
  onCheckoutComplete: PropTypes.func,
  onCardSelectClose: PropTypes.func,
  allowAccountCardReuse: PropTypes.bool,
  resetSelectedCategoryOnClose: PropTypes.bool,
  enableHolidayCategories: PropTypes.bool,
};

const AccountCardConfigurator = ({
  className,
  onExit,
  accountCardId,
  accountCardAlbumFolderId,
  onAccountCardChange,
  presetCardElementConfigurationInputs,
  photosContext,
  defaultCardCategoryId,
  defaultParentCardCategoryId,
  defaultReviewingMode = false,
  sharingEnabled = true,
  title = 'Create and mail a personalized card in a few taps!',
  subtitle = '',
  hideHeader,
  onAccountCardChanged,
  onReviewModeChanged,
  allowReviewing,
  reviewProps,
  onAddToCart,
  hasCartItems,
  mappings,
  onCheckoutSuccess,
  onCheckoutComplete,
  onCardSelectClose,
  allowAccountCardReuse = true,
  resetSelectedCategoryOnClose = true,
  watermarkSettings,
  enableHolidayCategories,
}) => {
  const basename = useBasename();
  const openModal = useOpenModal();
  const hasSeenBackSideSelection = useRef(false);
  const isNativeWebView = useIsNativeWebView();
  const [selectedCardCategory, setSelectedCardCategory] = useState(
    defaultCardCategoryId ? { id: defaultCardCategoryId } : null,
  );
  const [canSelectCardDesign, setCanSelectCardDesign] = useState(true);
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [side, setSide] = useState(CARD_SIDE.FRONT);
  const [reviewing, setReviewing] = useState(defaultReviewingMode);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const [stickerAlertDismissed, setStickerAlertDismissed] = useState(false);
  const [stampAlertDismissed, setStampAlertDismissed] = useState(false);
  const [cardFormsCompleted, setCardFormsCompleted] = useState([]);
  const [isClonedCard, setIsClonedCard] = useState(false);

  // Ensure this effect is run *before* onAccountCardChange. This way,
  // review changes will not override accountCard update changes.
  useEffect(() => {
    onReviewModeChanged?.(reviewing);
  }, [reviewing]);

  const {
    onCardChange,
    onElementConfigurationChange,
    onAccountCardDelete,
    onConfigurationReset,
    accountCard,
    authenticated,
    cardElementLoading,
    onUseExistingCard,
    onCardElementConfigurationDelete,
  } = useAccountCardConfigure({
    accountCardId,
    accountCardAlbumFolderId,
    setSelectedCardCategory,
    onAccountCardChange,
    presetCardElementConfigurationInputs,
  });

  const { loadingWaldograms, sentAccountCards = [] } = useAccountCardCreations({
    limit: 5,
  });

  const [selectedRosterIdentityIds, setSelectedRosterIdentityIds] = useState(
    photosContext?.roster_identity_ids ?? [],
  );
  const [cardSelectModalOpen, setCardSelectModalOpen] = useState(
    !accountCard && !accountCardId,
  );
  const [backCardSideSelectModalOpen, setBackCardSideSelectModalOpen] =
    useState(false);

  const [recipientSelectionModalOpen, setRecipientSelectionModalOpen] =
    useState(false);
  const [stickerAlertOpen, setStickerAlertOpen] = useState(false);
  const [stickerModalOpen, setStickerModalOpen] = useState(false);
  const [stampAlertOpen, setStampAlertOpen] = useState(false);
  const [stampModalOpen, setStampModalOpen] = useState(false);

  const {
    pricing,
    loadingPricing,
    canCheckout,
    checkingOut,
    checkoutSuccess,
    onCheckout,
    onCheckoutReset,
    paymentSummaryOpen,
    setPaymentSummaryOpen,
    accountCardCheckoutInput,
    promoCode,
    setPromoCode,
    globalAccountCard,
  } = useAccountCardCheckout({
    accountCard,
    onCheckoutSuccess,
  });

  const state = useMemo(
    () =>
      accountCard
        ? getAccountCardElementsConfiguration(accountCard, cardElementLoading)
        : null,
    [accountCard, cardElementLoading],
  );

  const { onVirtualPageViewTrack } = useVirtualPageView({
    fireOnMount: false,
  });

  const accountCardBackSide = accountCard?.account_card_sides?.find(
    ({ side: accountCardSide }) => accountCardSide === CARD_SIDE.BACK,
  )?.card_side;

  // Pop the back side selection modal open when the
  // user first turns the card.
  useEffect(() => {
    if (
      side === CARD_SIDE.BACK &&
      canSelectBackCardSideGroups(accountCard) &&
      !hasSeenBackSideSelection.current &&
      // Don't show the selector if this is a draft/sent
      !accountCardId
    ) {
      hasSeenBackSideSelection.current = true;
      setTimeout(() => {
        setBackCardSideSelectModalOpen(true);
      }, 300);
    }

    if (accountCard) {
      onVirtualPageViewTrack({
        pageViewTitle: `Create a card - Edit the ${side.toLowerCase()} side`,
        pageViewPath: `/side/${side}`,
      });

      if (
        !canSelectBackCardSideGroups(accountCard) &&
        hasPendingLabeledTextElementsOnSide(side, state)
      ) {
        setFormModalOpen(true);
      }
    }
  }, [side]);

  useEffect(() => {
    if (
      accountCardBackSide &&
      hasPendingLabeledTextElementsOnSide(side, state)
    ) {
      setFormModalOpen(true);
    }
  }, [accountCardBackSide]);

  useEffect(() => {
    onAccountCardChanged?.({
      accountCard,
      globalAccountCard,
      setDismissModalOpen,
      selectedCardCategory,
      setSelectedCardCategory,
      setReviewing,
    });
  }, [selectedCardCategory, accountCard, globalAccountCard]);

  const classes = classNames({
    'account-card-configurator': true,
    'is-configuring': !!selectedCardCategory && !reviewing,
    'is-reviewing': reviewing,
    [className]: !!className,
  });

  const addressElement = useMemo(
    () =>
      (state ?? []).find(
        element => element.cardElement.type === CARD_ELEMENT_TYPE.ADDRESS,
      ),
    [state],
  );
  const stickerElements = useMemo(
    () =>
      (state ?? [])
        .filter(
          element => element.cardElement.type === CARD_ELEMENT_TYPE.STICKER,
        )
        .filter(
          element =>
            !element.cardElementConfiguration?.system_sticker &&
            isCardElementEditable(element),
        ),
    [state],
  );
  const stampElement = useMemo(
    () =>
      (state ?? [])
        .filter(
          element =>
            element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
            element.side === CARD_SIDE.BACK,
        )
        .sort((a, b) => {
          if (a.cardElement.frame.x > b.cardElement.frame.x) {
            return -1;
          }

          if (b.cardElement.frame.x > a.cardElement.frame.x) {
            return 1;
          }

          return 0;
        })?.[0],
    [state],
  );

  // For empty stickers, we only care for them if we can actually edit them
  const emptyStickerElements = useMemo(
    () =>
      stickerElements.filter(
        element =>
          !element.cardElementConfiguration?.system_sticker &&
          isCardElementEditable(element),
      ),
    [stickerElements],
  );

  const stickerElement = emptyStickerElements?.[0];

  useEffect(() => {
    //  Ensure that if account card exist, and addressElement is set,
    //  we do not try to show the modal to the user.
    const { cardElementConfiguration } = addressElement ?? {};

    //  If there's no addresses at all, reset reviewing
    if (
      cardElementConfiguration?.external_shipping_addresses?.total === 0 &&
      cardElementConfiguration?.roster_identity_linked_identity_addresses
        ?.total === 0
    ) {
      setReviewing(false);
    }
  }, [accountCard]);

  useEffect(() => {
    const { cardElementConfiguration } = addressElement ?? {};

    //  If this is a cloned card without addresses, turn it back and open address modal
    if (
      isClonedCard &&
      cardElementConfiguration?.external_shipping_addresses?.total === 0 &&
      cardElementConfiguration?.roster_identity_linked_identity_addresses
        ?.total === 0
    ) {
      setSide(addressElement.side);
      setRecipientSelectionModalOpen(true);
      setIsClonedCard(false);
    }
  }, [addressElement, isClonedCard]);

  const resetConfigurator = (
    { resetCategory = true } = { resetCategory: true },
  ) => {
    setAddToCartSuccess(false);
    setDismissModalOpen(false);
    if (resetCategory) {
      setSelectedCardCategory(null);
    } else if (canSelectCardDesign) {
      setCardSelectModalOpen(true);
    }
    onConfigurationReset();
    onCheckoutReset();
    setReviewing(false);
  };

  return (
    <div className={classes}>
      {selectedCardCategory && !reviewing && (
        <Fragment>
          {!hideHeader && (
            <div className="account-card-configurator-header">
              <DismissButton
                onClick={() => {
                  if (accountCard) {
                    setDismissModalOpen(true);
                  } else {
                    if (isNativeWebView) {
                      postNativeAppCardsConfiguratorDismiss();
                      return;
                    }

                    setSelectedCardCategory(null);
                  }
                }}
              />
              <h4 className="account-card-configurator-title">
                Create your Card
              </h4>
            </div>
          )}

          {accountCard && (
            <AccountCard
              accountCard={accountCard}
              onElementConfigurationChange={onElementConfigurationChange}
              onCardElementConfigurationDelete={
                onCardElementConfigurationDelete
              }
              photosContext={
                selectedRosterIdentityIds.length > 0
                  ? {
                      ...(photosContext ?? {}),
                      roster_identity_ids: selectedRosterIdentityIds,
                    }
                  : photosContext
              }
              side={side}
              onSideChange={() => {
                setSide(prev =>
                  prev === CARD_SIDE.FRONT ? CARD_SIDE.BACK : CARD_SIDE.FRONT,
                );
              }}
              setSelectedRosterIdentityIds={setSelectedRosterIdentityIds}
              cardElementLoading={cardElementLoading}
              mappings={mappings}
              disablePhotoWatermarkOverlay
            />
          )}

          <div className="account-card-configurator-actions">
            {canSelectCardDesign && accountCard && side === CARD_SIDE.FRONT && (
              <Button
                btnOutline
                btnBlock
                onClick={() => {
                  setCardSelectModalOpen(true);
                }}
              >
                Change Template
              </Button>
            )}

            {side === CARD_SIDE.BACK &&
              canSelectBackCardSideGroups(accountCard) && (
                <Button
                  btnOutline
                  btnBlock
                  onClick={() => {
                    setBackCardSideSelectModalOpen(true);
                  }}
                >
                  Change card back
                </Button>
              )}

            {((canCheckout && pricing) || allowReviewing) && (
              <div className="account-card-send-button">
                <Button
                  btnBlock
                  data-testid="account-card-send-button"
                  onClick={event => {
                    if (isNativeWebView) {
                      event.preventDefault();
                      event.stopPropagation();

                      postNativeAppCardsConfiguratorComplete(accountCard.id);
                      return;
                    }

                    // Only show the Alert if all stickers all empty
                    if (
                      !stampAlertDismissed &&
                      !stampElement?.cardElementConfiguration?.photo &&
                      !stampElement?.cardElementConfiguration?.asset_image
                    ) {
                      setStampAlertOpen(true);
                    } else if (
                      !stickerAlertDismissed &&
                      stickerElements.length > 0 &&
                      stickerElements.length === emptyStickerElements.length &&
                      !stickerElement?.cardElementConfiguration?.system_sticker
                    ) {
                      setStickerAlertOpen(true);
                    } else {
                      setReviewing(true);
                    }
                  }}
                >
                  I&apos;m all set, continue
                </Button>
              </div>
            )}
          </div>

          <CardSelect
            forceOpen={cardSelectModalOpen}
            cardCategoryId={selectedCardCategory.id}
            onClose={individualCard => {
              if (onCardSelectClose) {
                onCardSelectClose();
                return;
              }
              if (!accountCard && !individualCard) {
                if (isNativeWebView) {
                  postNativeAppCardsConfiguratorDismiss();
                  return;
                }

                setSelectedCardCategory(null);
              }

              setCanSelectCardDesign(!individualCard);
              setCardSelectModalOpen(false);
            }}
            onCardSelect={async input => {
              await onCardChange(input);
              setCardSelectModalOpen(false);
            }}
            hasAccountCard={!!accountCard}
          />

          {accountCard && (
            <CardSideSelect
              forceOpen={backCardSideSelectModalOpen}
              side={CARD_SIDE.BACK}
              title="Choose a card back"
              accountCard={accountCard}
              onCardSideSelect={selectedCardSide =>
                onCardChange({
                  back_card_side_id: selectedCardSide.id,
                })
              }
              onClose={() => setBackCardSideSelectModalOpen(false)}
            />
          )}
        </Fragment>
      )}
      {!selectedCardCategory && !reviewing && (
        <Fragment>
          {onExit && (
            <button className="account-card-button-back" onClick={onExit}>
              <span className="account-card-button-back-icon">
                <Angle direction="left" />
              </span>
              Back
            </button>
          )}
          {title && <h3 className="text-black text-center">{title}</h3>}
          {subtitle}
          <CardCategories
            enableHolidayCategories={enableHolidayCategories}
            parentCardCategoryId={defaultParentCardCategoryId}
            onCategorySelect={cardCategory => {
              setSelectedCardCategory(cardCategory);
              setCardSelectModalOpen(true);

              if (side === CARD_SIDE.BACK) {
                setSide(CARD_SIDE.FRONT);
              }
            }}
          />

          {!loadingWaldograms &&
            sentAccountCards?.total > 0 &&
            allowAccountCardReuse && (
              <>
                <h3 className="mt-1">Your sent cards</h3>
                <AccountCardList
                  className="account-card-configurator-card-list"
                  accountCards={sentAccountCards.records}
                  canEditAccountCards={false}
                  canCloneAccountCards
                  onAccountCardCloned={clonedAccountCard => {
                    // Ensure we close down every popup:
                    hasSeenBackSideSelection.current = true;
                    setCardSelectModalOpen(false);
                    setIsClonedCard(true);
                    onUseExistingCard(clonedAccountCard);
                  }}
                />
              </>
            )}
        </Fragment>
      )}
      {reviewing && (
        <AccountCardReview
          onAddRecipients={() => setRecipientSelectionModalOpen(true)}
          accountCard={accountCard}
          cardElementLoading={cardElementLoading}
          onEdit={() => setReviewing(false)}
          onCheckout={() => {
            setPaymentSummaryOpen(true);
          }}
          onAddToCart={
            onAddToCart
              ? () => {
                  onAddToCart({ accountCard, globalAccountCard, pricing });
                  if (!hasCartItems) {
                    setAddToCartSuccess(true);
                  }
                }
              : undefined
          }
          hasCartItems={hasCartItems}
          checkingOut={checkingOut}
          onElementConfigurationChange={onElementConfigurationChange}
          {...reviewProps}
        />
      )}
      <AccountCardDismissConfirm
        forceOpen={dismissModalOpen}
        onCancel={() => setDismissModalOpen(false)}
        onConfirm={
          authenticated
            ? () => {
                if (isNativeWebView) {
                  postNativeAppCardsConfiguratorDismiss();
                  return;
                }

                resetConfigurator({
                  resetCategory: !!resetSelectedCategoryOnClose,
                });
              }
            : undefined
        }
        onDiscard={async () => {
          if (authenticated) {
            await onAccountCardDelete();

            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
              return;
            }

            resetConfigurator({
              resetCategory: !!resetSelectedCategoryOnClose,
            });
          } else {
            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
              return;
            }

            resetConfigurator({
              resetCategory: !!resetSelectedCategoryOnClose,
            });
          }
        }}
        message={
          authenticated ? (
            <p>You can save your card as a draft or discard your card.</p>
          ) : (
            <p>If you close this, you will lose your card&rsquo;s progress.</p>
          )
        }
      />
      <AccountCardStickerConfirm
        forceOpen={stickerAlertOpen}
        onConfirm={() => {
          setStickerAlertOpen(false);
          setStickerAlertDismissed(true);
          setStickerModalOpen(true);
          // Turn the card to the position of the sticker element.
          if (stickerElement?.side) {
            setSide(stickerElement.side);
          }
        }}
        onDismiss={() => {
          setStickerAlertOpen(false);
          setStickerAlertDismissed(true);
          setReviewing(true);
        }}
      />
      <AccountCardStampConfirm
        forceOpen={stampAlertOpen}
        onConfirm={() => {
          setStampAlertOpen(false);
          setStampAlertDismissed(true);
          setStampModalOpen(true);
          // Turn the card to the position of the sticker element.
          if (stampElement?.side) {
            setSide(stampElement.side);
          }
        }}
        onDismiss={() => {
          setStampAlertOpen(false);
          setStampAlertDismissed(true);
          // We do not want to show sticker alert if the user does not want a stamp
          setStickerAlertDismissed(true);

          if (
            !stickerAlertDismissed &&
            stickerElements.length > 0 &&
            stickerElements.length === emptyStickerElements.length &&
            !stickerElement?.cardElementConfiguration?.system_sticker
          ) {
            setStickerAlertOpen(true);
          } else {
            setReviewing(true);
          }
        }}
      />
      {accountCard && (
        <AccountCardAddressBookTable
          forceOpen={
            recipientSelectionModalOpen && basename !== BASENAME.SHUTTERBUG
          }
          onClose={() => setRecipientSelectionModalOpen(false)}
          addresses={
            addressElement?.cardElementConfiguration
              ?.roster_identity_linked_identity_addresses?.records ?? []
          }
          defaultFamilyName={
            !!addressElement?.cardElementConfiguration?.use_family_name
          }
          onAddRecipients={(selectedRosterIdentities, useFamilyName) => {
            const rosterIdentityLinkedIdentities =
              mapRosterIdentitiesToCustomPrintRecipients(
                selectedRosterIdentities,
              );
            // Set the selected roster identities
            setSelectedRosterIdentityIds(
              rosterIdentityLinkedIdentities.map(
                ({ rosterIdentityId }) => rosterIdentityId,
              ),
            );
            const addressesIds = rosterIdentityLinkedIdentities
              .map(({ address }) => address?.id)
              .filter(Boolean);

            if (addressElement) {
              onElementConfigurationChange(addressElement, {
                roster_identity_linked_identity_address_ids: addressesIds,
                use_family_name: !!useFamilyName,
              });
            }
            setRecipientSelectionModalOpen(false);
          }}
        />
      )}
      {accountCard && (
        <Fragment>
          <CardElementAddressControl
            forceOpen={
              recipientSelectionModalOpen && basename === BASENAME.SHUTTERBUG
            }
            onClose={() => setRecipientSelectionModalOpen(false)}
            addresses={
              addressElement?.cardElementConfiguration
                ?.external_shipping_addresses?.records ?? []
            }
            defaultFamilyName={
              !!addressElement?.cardElementConfiguration?.use_family_name
            }
            accountCard={accountCard}
            onChange={input => {
              if (addressElement) {
                onElementConfigurationChange(addressElement, {
                  external_shipping_address_ids: input.addresses.map(
                    ({ id }) => id,
                  ),
                  use_family_name: !!input.familyName,
                });
              }
              setRecipientSelectionModalOpen(false);
            }}
          />

          <CardElementStickerControl
            forceOpen={stickerModalOpen}
            onClose={() => {
              setStickerModalOpen(false);
            }}
            onChange={newSystemSticker => {
              if (stickerElement) {
                onElementConfigurationChange(stickerElement, {
                  system_sticker_id: newSystemSticker.id,
                });
              }
            }}
          />

          <CardElementPhotoSelectionControl
            forceOpen={stampModalOpen}
            onClose={() => {
              setStampModalOpen(false);
            }}
            onChange={value => {
              // Note that `value` here can be either an AssetImage (just uploaded)
              // or a Photo.
              const config = {};

              if (value.thumbnail_urls) {
                config.asset_image_id = null;
                config.photo_id = value.id;
              } else {
                config.asset_image_id = value.id;
                config.photo_id = null;
              }

              onElementConfigurationChange(stampElement, config);

              // if (
              //   !stickerAlertDismissed &&
              //   stickerElements.length > 0 &&
              //   stickerElements.length === emptyStickerElements.length &&
              //   !stickerElement?.cardElementConfiguration?.system_sticker
              // ) {
              //   setStickerAlertOpen(true);
              // } else {
              //   setReviewing(true);
              // }
            }}
            watermarkSettings={watermarkSettings}
          />

          <CardElementStickerControl
            forceOpen={stickerModalOpen}
            onClose={() => {
              setStickerModalOpen(false);
            }}
            onChange={newSystemSticker => {
              if (stickerElement) {
                onElementConfigurationChange(stickerElement, {
                  system_sticker_id: newSystemSticker.id,
                });
              }
            }}
          />

          <div>
            <AccountCardForm
              side={side}
              forceOpen={formModalOpen}
              elementsState={state}
              onClose={() => {
                setFormModalOpen(false);
                if (cardFormsCompleted.includes(side)) {
                  setCardFormsCompleted([...cardFormsCompleted, side]);
                }
              }}
              onElementConfigurationChange={onElementConfigurationChange}
            />
          </div>
        </Fragment>
      )}
      <AccountCardCheckoutPayment
        forceOpen={paymentSummaryOpen}
        onClose={() => setPaymentSummaryOpen(false)}
        pricing={pricing}
        onCheckout={onCheckout}
        loadingPricing={loadingPricing}
        submitting={checkingOut}
        accountCard={accountCard}
        accountCardCheckoutInput={accountCardCheckoutInput}
        onPromoCodeSubmit={value => setPromoCode(value)}
        defaultPromoCode={promoCode}
      />
      <AccountCardCheckoutSuccessAlert
        forceOpen={checkoutSuccess}
        onComplete={() => {
          resetConfigurator();
          onCheckoutComplete?.();
        }}
        sharingEnabled={sharingEnabled}
      />
      <AccountCardAddToCartSuccess
        forceOpen={addToCartSuccess}
        onCheckout={() => {
          setAddToCartSuccess(false);
          openModal(CHECKOUT_CART_MODAL);
        }}
        onMakeAnother={() => {
          resetConfigurator();
        }}
        onClose={() => {
          resetConfigurator();
        }}
      />
      <ContactSpreadsheetImportModal />
    </div>
  );
};

AccountCardConfigurator.propTypes = propTypes;

export default AccountCardConfigurator;
